import { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import {
  TextField, Button, Grid, Typography, Chip, Box, Select, MenuItem,
  FormControl, CircularProgress, Collapse, IconButton
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import {
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Add as AddIcon, Close as CloseIcon, Map as MapIcon
} from '@material-ui/icons'
import DisposalRouteMap from './DisposalRouteMap'
import DisposalRouteCompareMap, { PLAN_COLORS } from './DisposalRouteCompareMap'

const MATERIAL_CATEGORIES = [
  'concrete', 'dirt', 'rock', 'asphalt', 'tile',
  'green_waste', 'c_and_d', 'msw',
  'appliances', 'electronics', 'tires', 'scrap_metal',
  'special_waste', 'sludge', 'contaminated_soil'
]

const MATERIAL_LABELS = {
  concrete: 'Concrete', dirt: 'Dirt', rock: 'Rock', asphalt: 'Asphalt',
  tile: 'Tile', green_waste: 'Green Waste', c_and_d: 'C&D', msw: 'MSW',
  appliances: 'Appliances', electronics: 'Electronics', tires: 'Tires',
  scrap_metal: 'Scrap Metal', special_waste: 'Special Waste',
  sludge: 'Sludge', contaminated_soil: 'Contaminated Soil'
}

const MATERIAL_COLORS = {
  concrete: { bg: '#eef0f1', color: '#546e7a' },
  dirt: { bg: '#f0ebe6', color: '#6d4c41' },
  rock: { bg: '#eceff1', color: '#607d8b' },
  asphalt: { bg: '#e8ecef', color: '#455a64' },
  tile: { bg: '#f0f2f3', color: '#78909c' },
  green_waste: { bg: '#dceedf', color: '#2f7a3e' },
  c_and_d: { bg: '#fbe8d6', color: '#b35d1a' },
  msw: { bg: '#f7dccf', color: '#a9341d' },
  appliances: { bg: '#ece2fa', color: '#6e3fb7' },
  electronics: { bg: '#e6ebfa', color: '#3454c1' },
  tires: { bg: '#e8e8e8', color: '#424242' },
  scrap_metal: { bg: '#ececec', color: '#757575' },
  special_waste: { bg: '#fce4ec', color: '#c62828' },
  sludge: { bg: '#efebe9', color: '#5d4037' },
  contaminated_soil: { bg: '#fbe9e7', color: '#bf360c' }
}

const SCENARIO_LABELS = { OUR_TRUCK_20T: '20T Truck', OUR_TRAILER_10T: '10T Trailer', ROLLOFF: 'Roll-Off' }
const MAX_SEARCH_MILES = 500
const COMBINED_PLANS_TO_SHOW = 3
const HMI_YARD = { lat: 33.43730763333812, lng: -112.11879940396678 }

function getJobsiteCoords (j) {
  const c = j?.location?.coordinates
  if (Array.isArray(c) && c.length === 2 && c[0] != null) return { lng: Number(c[0]), lat: Number(c[1]) }
  if (j?.latitude != null) return { lat: Number(j.latitude), lng: Number(j.longitude) }
  return null
}

function formatJobsiteLabel (j) {
  if (!j) return ''
  const name = j.name || j.address || 'Unnamed'
  const client = j.client?.companyName || ''
  const addr = [j.address, j.city, j.state, j.zip].filter(Boolean).join(', ')
  return [name, client, addr].filter(Boolean).join(' — ')
}

function formatMoney (n) {
  if (n == null) return '—'
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const matChipStyle = (cat) => {
  const mc = MATERIAL_COLORS[cat] || { bg: '#f0f0f0', color: '#666' }
  return { backgroundColor: mc.bg, color: mc.color, fontWeight: 600, border: `1px solid ${mc.color}22`, fontSize: 10 }
}

/**
 * Build combined plans: find facilities that accept ALL requested materials,
 * pick the cheapest scenario per material at each facility, sum total cost.
 */
function buildCombinedPlans (resultGroups) {
  if (!resultGroups || resultGroups.length <= 1) return null

  // Collect all facility IDs that appear in every material group
  const facilityByGroup = resultGroups.map(g => {
    const map = new Map()
    for (const opt of g.options) {
      const key = `${opt.facilityId}_${opt.scenario}`
      if (!map.has(key) || opt.totalCost < map.get(key).totalCost) {
        map.set(key, opt)
      }
    }
    return map
  })

  // Find facility+scenario combos that exist in ALL groups
  const firstGroup = facilityByGroup[0]
  const plans = []

  for (const [key, firstOpt] of firstGroup) {
    const allPresent = facilityByGroup.every(m => m.has(key))
    if (!allPresent) continue

    const materialBreakdown = resultGroups.map((g, i) => {
      const opt = facilityByGroup[i].get(key)
      return { material: g.material, quantity: g.quantity, ...opt }
    })

    const totalCost = materialBreakdown.reduce((sum, o) => sum + (o.totalCost || 0), 0)
    const totalDumpFees = materialBreakdown.reduce((sum, o) => sum + (o.dumpFees || 0), 0)
    const totalTransport = materialBreakdown.reduce((sum, o) => sum + (o.transportCost || 0), 0)
    const totalTrips = materialBreakdown.reduce((sum, o) => sum + (o.trips || 0), 0)
    // All trips go to same facility, so total miles = round-trip miles × total trips
    const routeDistanceMiles = firstOpt.routeDistanceMiles || 0
    const routeDurationMinutes = firstOpt.routeDurationMinutes || 0
    const totalMiles = Math.round(routeDistanceMiles * totalTrips * 10) / 10
    const totalMinutes = Math.round(routeDurationMinutes * totalTrips)

    plans.push({
      facilityId: firstOpt.facilityId,
      facilityName: firstOpt.facilityName,
      facilityType: firstOpt.facilityType,
      facilityAddress: firstOpt.facilityAddress,
      facilityDistanceMiles: firstOpt.facilityDistanceMiles,
      scenario: firstOpt.scenario,
      totalCost: Math.round(totalCost * 100) / 100,
      totalDumpFees: Math.round(totalDumpFees * 100) / 100,
      totalTransport: Math.round(totalTransport * 100) / 100,
      totalTrips,
      totalMiles,
      totalMinutes,
      materialBreakdown
    })
  }

  plans.sort((a, b) => a.totalCost - b.totalCost)
  return plans.slice(0, COMBINED_PLANS_TO_SHOW)
}

/**
 * Build a split plan: send each material to its cheapest facility independently.
 * Returns null if single-material or if it's not cheaper than the best combined plan.
 */
function buildSplitPlan (resultGroups) {
  if (!resultGroups || resultGroups.length <= 1) return null

  const materialBreakdown = resultGroups.map(g => {
    const cheapest = (g.options || []).reduce((b, o) => !b || o.totalCost < b.totalCost ? o : b, null)
    if (!cheapest) return null
    return { material: g.material, quantity: g.quantity, ...cheapest }
  }).filter(Boolean)

  if (materialBreakdown.length !== resultGroups.length) return null

  // Group materials going to the same facility+scenario to consolidate trips
  const facilityGroups = new Map()
  for (const mb of materialBreakdown) {
    const key = `${mb.facilityId}_${mb.scenario}`
    if (!facilityGroups.has(key)) {
      facilityGroups.set(key, {
        facilityId: mb.facilityId, facilityName: mb.facilityName, scenario: mb.scenario,
        vehicleCapacity: mb.vehicleCapacity, routeDistanceMiles: mb.routeDistanceMiles || 0,
        routeDurationMinutes: mb.routeDurationMinutes || 0, costPerMile: mb.costPerMile || 0,
        operatorCostPerHour: mb.operatorCostPerHour || 0,
        materials: [], totalTons: 0, totalDumpFees: 0
      })
    }
    const grp = facilityGroups.get(key)
    grp.materials.push(mb)
    grp.totalTons += Number(mb.quantity) || 0
    grp.totalDumpFees += mb.dumpFees || 0
  }

  // Recalculate trips based on combined tonnage per facility
  let totalCost = 0, totalDumpFees = 0, totalTransport = 0, totalTrips = 0, totalMilesCalc = 0, totalMinutesCalc = 0
  const consolidatedBreakdown = []

  for (const grp of facilityGroups.values()) {
    const trips = Math.ceil(grp.totalTons / (grp.vehicleCapacity || 20))
    const milesPerTrip = grp.routeDistanceMiles
    const minutesPerTrip = grp.routeDurationMinutes
    const mileageCost = milesPerTrip * trips * grp.costPerMile
    const operatorCost = (minutesPerTrip / 60) * trips * grp.operatorCostPerHour
    const transportCost = mileageCost + operatorCost
    const facilityCost = transportCost + grp.totalDumpFees

    totalCost += facilityCost
    totalDumpFees += grp.totalDumpFees
    totalTransport += transportCost
    totalTrips += trips
    totalMilesCalc += milesPerTrip * trips
    totalMinutesCalc += minutesPerTrip * trips

    // Add each material as a line but with consolidated trip info
    for (const mb of grp.materials) {
      consolidatedBreakdown.push({
        ...mb,
        consolidatedTrips: trips,
        consolidatedTons: grp.totalTons,
        consolidatedWith: grp.materials.length > 1 ? grp.materials.filter(m => m !== mb).map(m => m.material) : []
      })
    }
  }

  const facilityNames = [...new Set(materialBreakdown.map(m => m.facilityName))]

  return {
    type: 'split',
    totalCost: Math.round(totalCost * 100) / 100,
    totalDumpFees: Math.round(totalDumpFees * 100) / 100,
    totalTransport: Math.round(totalTransport * 100) / 100,
    totalTrips,
    totalMiles: Math.round(totalMilesCalc * 10) / 10,
    totalMinutes: Math.round(totalMinutesCalc),
    stops: facilityNames.length,
    facilityNames,
    materialBreakdown: consolidatedBreakdown
  }
}

const DisposalQuickCalculator = ({ url }) => {
  const [locationInput, setLocationInput] = useState('')
  const [locationOptions, setLocationOptions] = useState([])
  const [locationLoading, setLocationLoading] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState(null) // { type: 'jobsite'|'address', lat, lng, label, ... }
  const debounceRef = useRef(null)

  const [materialRows, setMaterialRows] = useState([{ material: 'concrete', quantity: '' }])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resultGroups, setResultGroups] = useState(null)
  const [mixedLoadResults, setMixedLoadResults] = useState(null)
  const [expandedKey, setExpandedKey] = useState(null)
  const [showPerMaterial, setShowPerMaterial] = useState(false)
  const [compareOpen, setCompareOpen] = useState(false)

  // Unified search: jobsites + address geocoding in parallel
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!locationInput || locationInput.length < 2) { setLocationOptions([]); return }
    setLocationLoading(true)
    debounceRef.current = setTimeout(() => {
      const jobsiteP = axios.get(`${url}/api/customer/jobsites/search`, { params: { q: locationInput } })
        .then(res => {
          const data = Array.isArray(res.data?.jobsites) ? res.data.jobsites : []
          return data.filter(s => Boolean(getJobsiteCoords(s))).map(s => {
            const coords = getJobsiteCoords(s)
            return { type: 'jobsite', lat: coords.lat, lng: coords.lng, label: formatJobsiteLabel(s), raw: s }
          })
        })
        .catch(() => [])

      const addressP = locationInput.trim().length >= 3
        ? axios.get(`${url}/api/water-access/address-search`, { params: { q: locationInput.trim() } })
            .then(res => (res.data?.suggestions || []).map(s => ({
              type: 'address', lat: s.lat, lng: s.lng, label: s.formatted
            })))
            .catch(() => [])
        : Promise.resolve([])

      Promise.all([jobsiteP, addressP]).then(([jobsites, addresses]) => {
        const combined = []
        if (jobsites.length > 0) combined.push(...jobsites)
        if (addresses.length > 0) combined.push(...addresses)
        setLocationOptions(combined)
      }).finally(() => setLocationLoading(false))
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [locationInput, url])

  const updateRow = (idx, field, value) => setMaterialRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  const addRow = () => {
    const used = new Set(materialRows.map(r => r.material))
    const next = MATERIAL_CATEGORIES.find(c => !used.has(c)) || 'dirt'
    setMaterialRows(prev => [...prev, { material: next, quantity: '' }])
  }
  const removeRow = (idx) => { if (materialRows.length > 1) setMaterialRows(prev => prev.filter((_, i) => i !== idx)) }

  const validRows = materialRows.filter(r => r.material && r.quantity && Number(r.quantity) > 0)
  const canCalculate = selectedLocation && validRows.length > 0

  const handleCalculate = async () => {
    if (!canCalculate) return
    const coords = selectedLocation
    if (!coords?.lat) { setError('No coordinates available'); return }
    setLoading(true); setError(''); setResultGroups(null); setMixedLoadResults(null); setExpandedKey(null); setShowPerMaterial(false)
    try {
      const perMaterialPromises = validRows.map(row =>
        axios.post(`${url}/api/aggregate-dashboard/fulfillment-calc/disposal`, {
          materialCategory: row.material, quantity: Number(row.quantity),
          jobsiteLat: coords.lat, jobsiteLng: coords.lng, maxDistMiles: MAX_SEARCH_MILES
        }).then(resp => ({
          material: row.material, quantity: row.quantity,
          options: resp.data.options || [], cheapest: resp.data.cheapest,
          rolloffOptions: resp.data.rolloffOptions || []
        })).catch(err => ({
          material: row.material, quantity: row.quantity, options: [], cheapest: null,
          rolloffOptions: [],
          error: err.response?.data?.error || err.message
        }))
      )

      // Also fetch mixed-load options if multi-material
      const mixedLoadPromise = validRows.length > 1
        ? axios.post(`${url}/api/aggregate-dashboard/fulfillment-calc/disposal/mixed-load`, {
            materials: validRows.map(r => ({ category: r.material, quantity: Number(r.quantity) })),
            jobsiteLat: coords.lat, jobsiteLng: coords.lng, maxDistMiles: MAX_SEARCH_MILES
          }).then(resp => resp.data?.options || []).catch(() => [])
        : Promise.resolve([])

      const [groups, mixed] = await Promise.all([Promise.all(perMaterialPromises), mixedLoadPromise])
      setResultGroups(groups)
      setMixedLoadResults(mixed.length > 0 ? mixed : null)
    } catch (err) { setError(err.response?.data?.error || err.message) }
    finally { setLoading(false) }
  }

  const combinedPlans = useMemo(() => buildCombinedPlans(resultGroups), [resultGroups])
  const splitPlan = useMemo(() => buildSplitPlan(resultGroups), [resultGroups])
  const isMultiMaterial = resultGroups && resultGroups.length > 1
  const totalTons = validRows.reduce((s, r) => s + Number(r.quantity || 0), 0)

  // Summary stats for header cards
  const summary = useMemo(() => {
    if (!resultGroups) return null

    const bestMixed = mixedLoadResults?.[0] || null

    // Best "our truck" = compare combined (1 stop), split (multi-stop), mixed load, single-material
    let bestHaulCost = null
    let bestHaulLabel = ''
    let altHaulCost = null
    let altHaulLabel = ''
    if (isMultiMaterial) {
      const candidates = []
      if (bestMixed) candidates.push({ cost: bestMixed.totalCost, label: `Mixed: ${bestMixed.facilityName} (${bestMixed.trips} trip${bestMixed.trips !== 1 ? 's' : ''})`, type: 'mixed' })
      if (combinedPlans?.[0]) candidates.push({ cost: combinedPlans[0].totalCost, label: combinedPlans[0].facilityName, type: 'combined' })
      if (splitPlan) candidates.push({ cost: splitPlan.totalCost, label: `Split: ${splitPlan.stops} stops`, type: 'split' })
      candidates.sort((a, b) => a.cost - b.cost)
      if (candidates.length > 0) {
        bestHaulCost = candidates[0].cost
        bestHaulLabel = candidates[0].label
      }
      // Alternative: best single-stop option (mixed or combined) if cheapest is split,
      // or best split if cheapest is single-stop
      const cheapestType = candidates[0]?.type
      const altCandidates = candidates.filter(c => {
        if (cheapestType === 'split') return c.type === 'mixed' || c.type === 'combined'
        return c.type !== cheapestType
      })
      if (altCandidates.length > 0) {
        altHaulCost = altCandidates[0].cost
        altHaulLabel = altCandidates[0].label
      }
    } else {
      const cheapest = resultGroups[0]?.options?.[0]
      if (cheapest) {
        bestHaulCost = cheapest.totalCost
        bestHaulLabel = cheapest.facilityName
      }
    }

    // Best roll-off: sum cheapest per material (roll-offs don't care about facility routing)
    const allRolloffs = resultGroups.flatMap(g => g.rolloffOptions || [])
    let bestRolloffCost = null
    let bestRolloffLabel = ''
    if (allRolloffs.length > 0) {
      if (isMultiMaterial) {
        // Sum cheapest roll-off per material group
        let total = 0
        let valid = true
        for (const g of resultGroups) {
          const cheapest = (g.rolloffOptions || []).reduce((b, o) => !b || o.totalCost < b.totalCost ? o : b, null)
          if (!cheapest) { valid = false; break }
          total += cheapest.totalCost
        }
        if (valid) {
          bestRolloffCost = Math.round(total * 100) / 100
          bestRolloffLabel = 'Best combo'
        }
      } else {
        const cheapest = allRolloffs.reduce((b, o) => !b || o.totalCost < b.totalCost ? o : b, null)
        if (cheapest) {
          bestRolloffCost = cheapest.totalCost
          bestRolloffLabel = `${cheapest.vendorName} ${cheapest.containerSize}yd`
        }
      }
    }

    const facilitiesCount = new Set(resultGroups.flatMap(g => (g.options || []).map(o => o.facilityId))).size

    return {
      bestHaul: bestHaulCost != null ? { totalCost: bestHaulCost, label: bestHaulLabel } : null,
      altHaul: altHaulCost != null ? { totalCost: altHaulCost, label: altHaulLabel } : null,
      bestRolloff: bestRolloffCost != null ? { totalCost: bestRolloffCost, label: bestRolloffLabel } : null,
      facilitiesCount,
      rolloffCount: allRolloffs.length
    }
  }, [resultGroups, combinedPlans, splitPlan, mixedLoadResults, isMultiMaterial])

  return (
    <div style={{ padding: '0 8px' }}>
      {/* ── Input Section ─────────────────────────── */}
      <div style={{
        background: 'var(--surface, #fbf8f1)', borderRadius: 12, padding: '16px 20px',
        border: '1px solid var(--line, #e0d8c2)', marginBottom: 16
      }}>
        <Grid container spacing={2} alignItems="flex-end" style={{ marginBottom: 12 }}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              value={selectedLocation}
              onChange={(_, val) => setSelectedLocation(val)}
              inputValue={locationInput}
              onInputChange={(_, val) => setLocationInput(val)}
              options={locationOptions}
              loading={locationLoading}
              getOptionLabel={opt => opt?.label || ''}
              groupBy={opt => opt?.type === 'jobsite' ? 'Jobsites' : 'Addresses'}
              filterOptions={x => x}
              renderOption={opt => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3,
                    background: opt.type === 'jobsite' ? 'var(--primary-soft, #e3eee0)' : '#faf0d4',
                    color: opt.type === 'jobsite' ? 'var(--primary, #1f5e3a)' : '#8B6914'
                  }}>{opt.type === 'jobsite' ? 'JOB' : 'ADDR'}</span>
                  <span style={{ fontSize: 12 }}>{opt.label}</span>
                </div>
              )}
              renderInput={params => (
                <TextField {...params} label="Jobsite or Address" variant="outlined" size="small"
                  placeholder="Search jobsites or type an address..."
                  InputProps={{ ...params.InputProps, style: { fontSize: 13 },
                    endAdornment: (<>{locationLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>)
                  }}
                />
              )}
              noOptionsText={locationInput.length < 2 ? 'Type to search...' : 'No results found'}
            />
          </Grid>
          <Grid item style={{ flex: 1 }} />
          <Grid item>
            <Button variant="contained" disabled={!canCalculate || loading} onClick={handleCalculate}
              startIcon={loading ? <CircularProgress size={14} style={{ color: '#fbf8f1' }} /> : null}
              style={{
                background: canCalculate && !loading ? 'var(--primary, #1f5e3a)' : 'var(--ink-4, #97a094)',
                color: '#fbf8f1', textTransform: 'none', borderRadius: 10, fontWeight: 600,
                height: 40, minWidth: 140
              }}>
              {loading ? 'Calculating...' : 'Calculate'}
            </Button>
          </Grid>
        </Grid>

        <div style={{ background: 'var(--surface-2, #f6f1e3)', borderRadius: 8, padding: '10px 12px', border: '1px solid var(--line, #e0d8c2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--ink-3, #6f7a6b)', fontFamily: 'var(--font-mono)' }}>
              Materials to Haul
            </span>
            <Button size="small" startIcon={<AddIcon style={{ fontSize: 14 }} />} onClick={addRow}
              style={{ fontSize: 11, textTransform: 'none', color: 'var(--primary, #1f5e3a)', fontWeight: 600, padding: '2px 8px' }}>
              Add Material
            </Button>
          </div>
          {materialRows.map((row, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: idx < materialRows.length - 1 ? 6 : 0 }}>
              <FormControl variant="outlined" size="small" style={{ minWidth: 160, flex: 1 }}>
                <Select value={row.material} onChange={e => updateRow(idx, 'material', e.target.value)}
                  style={{ fontSize: 13, background: 'var(--surface, #fbf8f1)' }}>
                  {MATERIAL_CATEGORIES.map(cat => <MenuItem key={cat} value={cat}>{MATERIAL_LABELS[cat]}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField type="number" size="small" variant="outlined" placeholder="Tons"
                value={row.quantity} onChange={e => updateRow(idx, 'quantity', e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                InputProps={{ style: { fontSize: 13, background: 'var(--surface, #fbf8f1)' } }}
                style={{ width: 100 }} />
              <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>tons</span>
              {materialRows.length > 1 && (
                <IconButton size="small" onClick={() => removeRow(idx)} style={{ padding: 4 }}>
                  <CloseIcon style={{ fontSize: 16, color: 'var(--ink-4, #97a094)' }} />
                </IconButton>
              )}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <Box mb={2} p={1.5} style={{ background: 'var(--disposal-soft, #f7dccf)', borderRadius: 10, border: '1px solid rgba(169,52,29,.2)' }}>
          <Typography variant="body2" style={{ color: 'var(--disposal, #a9341d)', fontSize: 12 }}>{error}</Typography>
        </Box>
      )}

      {!resultGroups && !loading && !error && (
        <Box py={6} textAlign="center">
          <Typography variant="body2" style={{ color: 'var(--ink-4, #97a094)', fontSize: 13 }}>
            Select a jobsite, add materials with quantities, then calculate haul-off options
          </Typography>
        </Box>
      )}

      {/* ── Summary Cards ──────────────────────────── */}
      {summary && (() => {
        const isRolloffCheaper = summary.bestRolloff && (!summary.bestHaul || summary.bestRolloff.totalCost < summary.bestHaul.totalCost)
        const cheapest = isRolloffCheaper ? summary.bestRolloff : summary.bestHaul
        const cheapestType = isRolloffCheaper ? 'roll-off' : 'our truck'

        return (
          <div style={{
            display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap'
          }}>
            {/* Hero: Cheapest overall */}
            <div style={{
              flex: 2, minWidth: 200, background: 'var(--primary-soft, #e3eee0)', borderRadius: 10,
              padding: '12px 16px', border: '1px solid rgba(31,94,58,.15)', textAlign: 'center'
            }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--primary, #1f5e3a)', fontFamily: 'var(--font-mono)', letterSpacing: -0.5 }}>
                {formatMoney(cheapest?.totalCost)}
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-3, #6f7a6b)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>
                Cheapest — {cheapestType}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3, #6f7a6b)', marginTop: 2 }}>
                {cheapest?.label}
              </div>
            </div>
            {/* Alternative: best different approach */}
            {summary.altHaul && summary.altHaul.totalCost !== cheapest?.totalCost && (
              <div style={{
                flex: 1, minWidth: 140, background: 'var(--surface, #fbf8f1)', borderRadius: 10,
                padding: '12px 16px', border: '1px solid var(--line, #e0d8c2)', textAlign: 'center'
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-2, #3d463a)', fontFamily: 'var(--font-mono)' }}>
                  {formatMoney(summary.altHaul.totalCost)}
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4, #97a094)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>
                  Alternative
                </div>
                <div style={{ fontSize: 9, color: 'var(--ink-4, #97a094)', marginTop: 1 }}>{summary.altHaul.label}</div>
              </div>
            )}
            {/* Roll-off if different from cheapest */}
            {summary.bestRolloff && !isRolloffCheaper && (
              <div style={{
                flex: 1, minWidth: 120, background: '#faf6e8', borderRadius: 10,
                padding: '12px 16px', border: '1px solid rgba(139,105,20,.15)', textAlign: 'center'
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#8B6914', fontFamily: 'var(--font-mono)' }}>
                  {formatMoney(summary.bestRolloff.totalCost)}
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#a68b3c', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>
                  Roll-Off
                </div>
                <div style={{ fontSize: 9, color: 'var(--ink-4, #97a094)', marginTop: 1 }}>{summary.bestRolloff.label}</div>
              </div>
            )}
            {/* Savings between cheapest and alternative */}
            {summary.altHaul && summary.altHaul.totalCost > cheapest?.totalCost && (
              <div style={{
                flex: 1, minWidth: 100, background: 'var(--primary-soft, #e3eee0)', borderRadius: 10,
                padding: '12px 16px', border: '1px solid rgba(31,94,58,.15)', textAlign: 'center'
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary, #1f5e3a)', fontFamily: 'var(--font-mono)' }}>
                  {formatMoney(summary.altHaul.totalCost - cheapest.totalCost)}
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-4, #97a094)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>
                  Savings
                </div>
              </div>
            )}
          </div>
        )
      })()}

      {/* ── Compare Routes button + modal ──── */}
      {resultGroups && selectedLocation && isMultiMaterial && (mixedLoadResults?.length > 0 || combinedPlans?.length > 0 || splitPlan) && (() => {
        // Build plan list for comparison map
        const compPlans = []
        let colorIdx = 0
        if (mixedLoadResults?.[0]) {
          const m = mixedLoadResults[0]
          if (m.facilityLat) {
            compPlans.push({
              name: `Mixed: ${m.facilityName}`, type: 'mixed',
              color: PLAN_COLORS[colorIdx++],
              facilities: [{ lat: m.facilityLat, lng: m.facilityLng, name: m.facilityName }],
              cost: m.totalCost, trips: m.trips
            })
          }
        }
        if (combinedPlans) {
          for (const cp of combinedPlans) {
            const mb0 = cp.materialBreakdown?.[0]
            if (mb0?.facilityLat) {
              compPlans.push({
                name: cp.facilityName, type: 'combined',
                color: PLAN_COLORS[colorIdx++ % PLAN_COLORS.length],
                facilities: [{ lat: mb0.facilityLat, lng: mb0.facilityLng, name: cp.facilityName }],
                cost: cp.totalCost, trips: cp.totalTrips
              })
            }
          }
        }
        if (splitPlan) {
          // Collect unique facilities from split plan
          const facMap = new Map()
          for (const mb of splitPlan.materialBreakdown) {
            if (mb.facilityLat && !facMap.has(String(mb.facilityId))) {
              facMap.set(String(mb.facilityId), { lat: mb.facilityLat, lng: mb.facilityLng, name: mb.facilityName })
            }
          }
          if (facMap.size > 0) {
            compPlans.push({
              name: `Split: ${splitPlan.stops} stops`, type: 'split',
              color: PLAN_COLORS[colorIdx++ % PLAN_COLORS.length],
              facilities: [...facMap.values()],
              cost: splitPlan.totalCost, trips: splitPlan.totalTrips
            })
          }
        }

        if (compPlans.length < 2) return null
        return (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <Button size="small" variant="outlined" startIcon={<MapIcon style={{ fontSize: 16 }} />}
                onClick={() => setCompareOpen(true)}
                style={{
                  textTransform: 'none', fontSize: 11, fontWeight: 600, borderRadius: 8,
                  color: 'var(--primary, #1f5e3a)', borderColor: 'rgba(31,94,58,.3)'
                }}>
                Compare Routes ({compPlans.length})
              </Button>
            </div>
            <DisposalRouteCompareMap
              open={compareOpen} onClose={() => setCompareOpen(false)}
              url={url} hmiCoords={HMI_YARD}
              jobCoords={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
              plans={compPlans} formatMoney={formatMoney}
            />
          </>
        )
      })()}

      {/* ── Mixed Load (all materials in one truck) ──── */}
      {isMultiMaterial && mixedLoadResults && mixedLoadResults.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--primary, #1f5e3a)', fontFamily: 'var(--font-mono)' }}>
              MIXED LOAD — ONE TRUCK
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>
              {totalTons} tons total &middot; all materials combined &middot; facilities that accept everything
            </span>
          </div>

          {mixedLoadResults.slice(0, 3).map((opt, idx) => {
            const key = `mixed-${idx}`
            const isExpanded = expandedKey === key
            const isBest = idx === 0

            return (
              <div key={key} style={{
                background: isBest ? 'var(--primary-soft, #e3eee0)' : 'var(--surface, #fbf8f1)',
                border: `1px solid ${isBest ? 'rgba(31,94,58,.25)' : 'var(--line, #e0d8c2)'}`,
                borderRadius: 10, marginBottom: 8, overflow: 'hidden', cursor: 'pointer',
                boxShadow: isBest ? '0 1px 4px rgba(31,94,58,.12)' : 'none'
              }} onClick={() => setExpandedKey(isExpanded ? null : key)}>
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isBest ? 'var(--primary, #1f5e3a)' : 'var(--surface-2, #f6f1e3)',
                    color: isBest ? '#fbf8f1' : 'var(--ink-3, #6f7a6b)', fontSize: 12, fontWeight: 700, flexShrink: 0
                  }}>#{idx + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink, #1c2419)' }}>{opt.facilityName}</span>
                      <Chip size="small" label={SCENARIO_LABELS[opt.scenario] || opt.scenario}
                        variant="outlined" style={{ fontSize: 9, height: 18, fontWeight: 600, color: 'var(--ink-3, #6f7a6b)' }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4, #97a094)', marginTop: 3 }}>
                      {opt.facilityDistanceMiles} mi away &middot; {opt.trips} trip{opt.trips !== 1 ? 's' : ''} &middot; {opt.totalTons}T mixed &middot; {Math.round(opt.routeDistanceMiles * opt.trips)} total mi &middot; {Math.round(opt.routeDurationMinutes * opt.trips)} min drive
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{
                      fontSize: 20, fontWeight: 800, fontFamily: 'var(--font-mono)', letterSpacing: -0.5,
                      color: isBest ? 'var(--primary, #1f5e3a)' : 'var(--ink, #1c2419)'
                    }}>{formatMoney(opt.totalCost)}</div>
                    <div style={{ fontSize: 10, color: 'var(--ink-4, #97a094)' }}>
                      {formatMoney(opt.dumpFees)} dump &middot; {formatMoney(opt.transportCost)} transport
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, color: 'var(--ink-4, #97a094)' }}>
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </div>
                </div>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <div style={{ padding: '0 16px 12px', borderTop: '1px solid var(--line, #e0d8c2)', paddingTop: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 11, marginBottom: 8 }}>
                      <DetailRow label="Round-trip" value={`${opt.routeDistanceMiles} mi (${opt.routeDurationMinutes} min)`} />
                      <DetailRow label="Trips" value={`${opt.trips} × ${opt.vehicleCapacity}T capacity`} />
                      <DetailRow label="Mileage" value={`${formatMoney(opt.mileageCost)} at $${opt.costPerMile}/mi`} />
                      <DetailRow label="Operator" value={`${formatMoney(opt.operatorCost)} at $${opt.operatorCostPerHour}/hr`} />
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ink-3, #6f7a6b)', letterSpacing: '0.08em', marginBottom: 4, fontFamily: 'var(--font-mono)' }}>
                      Dump Fees by Material
                    </div>
                    {opt.materialBreakdown?.map((mb, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 11 }}>
                        <Chip size="small" label={MATERIAL_LABELS[mb.material]} style={matChipStyle(mb.material)} />
                        <span style={{ color: 'var(--ink-3, #6f7a6b)' }}>{mb.quantity}T</span>
                        <span style={{ flex: 1 }} />
                        <span style={{ color: 'var(--ink-2, #3d463a)' }}>
                          {mb.dumpFeeUnit === 'PER_TON' ? `$${mb.dumpFeeRate}/T` : mb.dumpFeeUnit === 'PER_LOAD' ? `$${mb.dumpFeeRate}/load` : '—'}
                        </span>
                      </div>
                    ))}
                    {opt.facilityAddress && (
                      <div style={{ fontSize: 11, color: 'var(--ink-4, #97a094)', marginTop: 8 }}>{opt.facilityAddress}</div>
                    )}
                    {selectedLocation && opt.facilityLat && (
                      <div style={{ marginTop: 10 }}>
                        <DisposalRouteMap url={url} hmiCoords={HMI_YARD}
                          jobCoords={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                          facilities={[{ lat: opt.facilityLat, lng: opt.facilityLng, name: opt.facilityName }]}
                          height={220} />
                      </div>
                    )}
                  </div>
                </Collapse>
              </div>
            )
          })}
        </div>
      )}

      {/* ── Combined Plans (multi-material) ────────── */}
      {isMultiMaterial && combinedPlans && combinedPlans.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-3, #6f7a6b)', fontFamily: 'var(--font-mono)' }}>
              BEST OPTIONS — ALL MATERIALS
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>
              {totalTons} tons total &middot; facilities that accept everything
            </span>
          </div>

          {combinedPlans.map((plan, pIdx) => {
            const key = `combined-${pIdx}`
            const isExpanded = expandedKey === key
            const isBest = pIdx === 0

            return (
              <div key={key} style={{
                background: isBest ? 'var(--primary-soft, #e3eee0)' : 'var(--surface, #fbf8f1)',
                border: `1px solid ${isBest ? 'rgba(31,94,58,.25)' : 'var(--line, #e0d8c2)'}`,
                borderRadius: 10, marginBottom: 8, overflow: 'hidden', cursor: 'pointer',
                boxShadow: isBest ? '0 1px 4px rgba(31,94,58,.12)' : 'none'
              }} onClick={() => setExpandedKey(isExpanded ? null : key)}>
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isBest ? 'var(--primary, #1f5e3a)' : 'var(--surface-2, #f6f1e3)',
                    color: isBest ? '#fbf8f1' : 'var(--ink-3, #6f7a6b)', fontSize: 12, fontWeight: 700, flexShrink: 0
                  }}>#{pIdx + 1}</div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink, #1c2419)' }}>{plan.facilityName}</span>
                      <Chip size="small" label={SCENARIO_LABELS[plan.scenario] || plan.scenario}
                        variant="outlined" style={{ fontSize: 9, height: 18, fontWeight: 600, color: 'var(--ink-3, #6f7a6b)' }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4, #97a094)', marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                      <span>{plan.facilityDistanceMiles} mi away</span>
                      <span>&middot;</span>
                      <span>{plan.totalTrips} trips</span>
                      <span>&middot;</span>
                      <span>{plan.totalMiles} total mi</span>
                      <span>&middot;</span>
                      <span>{plan.totalMinutes} min drive</span>
                      <span>&middot;</span>
                      {plan.materialBreakdown.map((mb, i) => (
                        <Chip key={i} size="small" label={`${MATERIAL_LABELS[mb.material]} ${mb.quantity}T`}
                          style={{ ...matChipStyle(mb.material), height: 16, fontSize: 9 }} />
                      ))}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{
                      fontSize: 20, fontWeight: 800, color: isBest ? 'var(--primary, #1f5e3a)' : 'var(--ink, #1c2419)',
                      fontFamily: 'var(--font-mono)', letterSpacing: -0.5
                    }}>{formatMoney(plan.totalCost)}</div>
                    <div style={{ fontSize: 10, color: 'var(--ink-4, #97a094)', marginTop: 1 }}>
                      {formatMoney(plan.totalDumpFees)} dump &middot; {formatMoney(plan.totalTransport)} transport
                    </div>
                  </div>

                  <div style={{ flexShrink: 0, color: 'var(--ink-4, #97a094)' }}>
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </div>
                </div>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <div style={{ padding: '0 16px 14px', borderTop: '1px solid var(--line, #e0d8c2)', paddingTop: 12 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ink-3, #6f7a6b)', letterSpacing: '0.08em', fontFamily: 'var(--font-mono)' }}>
                      Per-Material Breakdown
                    </span>
                    {plan.materialBreakdown.map((mb, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
                        borderBottom: i < plan.materialBreakdown.length - 1 ? '1px solid var(--line, #e0d8c2)' : 'none'
                      }}>
                        <Chip size="small" label={MATERIAL_LABELS[mb.material]} style={matChipStyle(mb.material)} />
                        <span style={{ fontSize: 12, color: 'var(--ink-2, #3d463a)' }}>
                          {mb.quantity} tons &middot; {mb.trips} trip{mb.trips !== 1 ? 's' : ''}
                        </span>
                        <span style={{ flex: 1 }} />
                        <span style={{ fontSize: 12, color: 'var(--ink-2, #3d463a)' }}>
                          {formatMoney(mb.dumpFees)} dump
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--ink-2, #3d463a)' }}>
                          {formatMoney(mb.transportCost)} transport
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink, #1c2419)', fontFamily: 'var(--font-mono)' }}>
                          {formatMoney(mb.totalCost)}
                        </span>
                      </div>
                    ))}
                    {plan.facilityAddress && (
                      <div style={{ fontSize: 11, color: 'var(--ink-4, #97a094)', marginTop: 8 }}>
                        {plan.facilityAddress}
                      </div>
                    )}
                    {selectedLocation && plan.materialBreakdown?.[0]?.facilityLat && (
                      <div style={{ marginTop: 10 }}>
                        <DisposalRouteMap url={url} hmiCoords={HMI_YARD}
                          jobCoords={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                          facilities={[{
                            lat: plan.materialBreakdown[0].facilityLat,
                            lng: plan.materialBreakdown[0].facilityLng,
                            name: plan.facilityName
                          }]}
                          height={220} />
                      </div>
                    )}
                  </div>
                </Collapse>
              </div>
            )
          })}

          {combinedPlans.length === 0 && (
            <Box py={2} px={2} style={{ background: 'var(--surface-2, #f6f1e3)', borderRadius: 8, border: '1px solid var(--line, #e0d8c2)' }}>
              <Typography variant="body2" style={{ color: 'var(--ink-4, #97a094)', fontSize: 12 }}>
                No single facility accepts all your materials. See per-material options below.
              </Typography>
            </Box>
          )}
        </div>
      )}

      {/* ── Split Plan (cheapest per material) ────── */}
      {isMultiMaterial && splitPlan && combinedPlans?.[0] && splitPlan.totalCost < combinedPlans[0].totalCost && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--primary, #1f5e3a)', fontFamily: 'var(--font-mono)' }}>
              CHEAPEST SPLIT — {splitPlan.stops} STOPS
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>
              saves {formatMoney(combinedPlans[0].totalCost - splitPlan.totalCost)} vs single stop
            </span>
          </div>

          <div style={{
            background: 'var(--primary-soft, #e3eee0)', border: '1px solid rgba(31,94,58,.25)',
            borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(31,94,58,.12)'
          }}>
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink, #1c2419)' }}>
                  Split across {splitPlan.facilityNames.join(' + ')}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-4, #97a094)', marginTop: 3 }}>
                  {splitPlan.totalTrips} trips &middot; {splitPlan.stops} stops &middot; {splitPlan.totalMiles} total mi &middot; {splitPlan.totalMinutes} min drive
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary, #1f5e3a)', fontFamily: 'var(--font-mono)' }}>
                  {formatMoney(splitPlan.totalCost)}
                </div>
                <div style={{ fontSize: 10, color: 'var(--ink-4, #97a094)' }}>
                  {formatMoney(splitPlan.totalDumpFees)} dump &middot; {formatMoney(splitPlan.totalTransport)} transport
                </div>
              </div>
            </div>
            <div style={{ padding: '0 16px 12px', borderTop: '1px solid rgba(31,94,58,.1)', paddingTop: 10 }}>
              {/* Group split plan materials by destination facility */}
              {(() => {
                const facilityMap = new Map()
                for (const mb of splitPlan.materialBreakdown) {
                  const fKey = mb.facilityId || mb.facilityName
                  if (!facilityMap.has(fKey)) {
                    facilityMap.set(fKey, {
                      facilityName: mb.facilityName, facilityId: mb.facilityId,
                      materials: [], totalDump: 0, totalTons: 0,
                      trips: mb.consolidatedTrips || mb.trips || 1,
                      routeDistanceMiles: mb.routeDistanceMiles || 0,
                      routeDurationMinutes: mb.routeDurationMinutes || 0,
                      facilityLat: null, facilityLng: null
                    })
                  }
                  const grp = facilityMap.get(fKey)
                  grp.materials.push(mb)
                  grp.totalDump += mb.dumpFees || 0
                  grp.totalTons += Number(mb.quantity) || 0
                  if (mb.facilityLat && !grp.facilityLat) {
                    grp.facilityLat = mb.facilityLat
                    grp.facilityLng = mb.facilityLng
                  }
                }
                const groups = [...facilityMap.values()]
                return groups.map((grp, gi) => (
                  <div key={gi} style={{
                    padding: '8px 0',
                    borderBottom: gi < groups.length - 1 ? '1px solid rgba(31,94,58,.08)' : 'none'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink, #1c2419)' }}>
                        {grp.facilityName}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3, #6f7a6b)' }}>
                        {grp.trips} trip{grp.trips !== 1 ? 's' : ''}
                      </span>
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: 11, color: 'var(--ink-3, #6f7a6b)' }}>
                        {formatMoney(grp.totalDump)} dump
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 4 }}>
                      {grp.materials.map((mb, mi) => (
                        <Chip key={mi} size="small"
                          label={`${MATERIAL_LABELS[mb.material]} ${mb.quantity}T`}
                          style={{ ...matChipStyle(mb.material), height: 20, fontSize: 10 }} />
                      ))}
                    </div>
                  </div>
                ))
              })()}
              {/* Route map for split plan — show all facility stops */}
              {selectedLocation && (() => {
                const facWithCoords = [...new Map(
                  splitPlan.materialBreakdown.filter(mb => mb.facilityLat)
                    .map(mb => [String(mb.facilityId), { lat: mb.facilityLat, lng: mb.facilityLng, name: mb.facilityName }])
                ).values()]
                if (facWithCoords.length === 0) return null
                return (
                  <div style={{ padding: '0 16px 12px' }}>
                    <DisposalRouteMap url={url} hmiCoords={HMI_YARD}
                      jobCoords={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                      facilities={facWithCoords}
                      routeColor="#E65100" height={240} />
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Roll-Off Comparison ─────────────────── */}
      {resultGroups && resultGroups.some(g => g.rolloffOptions?.length > 0) && (
        <RolloffComparison resultGroups={resultGroups} expandedKey={expandedKey} setExpandedKey={setExpandedKey} />
      )}

      {/* ── Single-material results OR per-material detail ── */}
      {resultGroups && (
        <div>
          {isMultiMaterial && (
            <Button size="small" onClick={() => setShowPerMaterial(!showPerMaterial)}
              style={{ fontSize: 11, textTransform: 'none', color: 'var(--ink-3, #6f7a6b)', fontWeight: 600, marginBottom: 8 }}>
              {showPerMaterial ? 'Hide' : 'Show'} per-material breakdown ({resultGroups.reduce((s, g) => s + g.options.length, 0)} options)
            </Button>
          )}

          {(!isMultiMaterial || showPerMaterial) && resultGroups.map((group, gIdx) => (
            <div key={gIdx} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <Chip size="small" label={MATERIAL_LABELS[group.material]} style={matChipStyle(group.material)} />
                <span style={{ fontSize: 11, color: 'var(--ink-3, #6f7a6b)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                  {group.quantity} TONS
                </span>
                <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>
                  &middot; {group.options.length} option{group.options.length !== 1 ? 's' : ''}
                </span>
              </div>

              {group.options.length === 0 && !group.error && (
                <Box py={2} px={2} style={{ background: 'var(--surface-2, #f6f1e3)', borderRadius: 8, border: '1px solid var(--line, #e0d8c2)' }}>
                  <Typography variant="body2" style={{ color: 'var(--ink-4, #97a094)', fontSize: 12 }}>
                    No facilities found that accept {MATERIAL_LABELS[group.material]}
                  </Typography>
                </Box>
              )}

              {group.options.slice(0, isMultiMaterial ? 5 : undefined).map((opt, oIdx) => {
                const key = `${gIdx}-${oIdx}`
                const isExpanded = expandedKey === key
                const isCheapest = oIdx === 0
                return (
                  <div key={key} style={{
                    background: isCheapest ? 'var(--primary-soft, #e3eee0)' : 'var(--surface, #fbf8f1)',
                    border: `1px solid ${isCheapest ? 'rgba(31,94,58,.25)' : 'var(--line, #e0d8c2)'}`,
                    borderRadius: 10, marginBottom: 6, overflow: 'hidden', cursor: 'pointer',
                    boxShadow: isCheapest ? '0 1px 4px rgba(31,94,58,.12)' : 'none'
                  }} onClick={() => setExpandedKey(isExpanded ? null : key)}>
                    <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isCheapest ? 'var(--primary, #1f5e3a)' : 'var(--surface-2, #f6f1e3)',
                        color: isCheapest ? '#fbf8f1' : 'var(--ink-3, #6f7a6b)', fontSize: 10, fontWeight: 700, flexShrink: 0
                      }}>#{opt.rank}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink, #1c2419)' }}>{opt.facilityName}</span>
                          <Chip size="small" label={SCENARIO_LABELS[opt.scenario] || opt.scenario}
                            variant="outlined" style={{ fontSize: 9, height: 16, fontWeight: 600, color: 'var(--ink-3, #6f7a6b)' }} />
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--ink-4, #97a094)', marginTop: 1 }}>
                          {opt.facilityDistanceMiles} mi &middot; {opt.trips} trip{opt.trips !== 1 ? 's' : ''} &middot; {Math.round((opt.routeDistanceMiles || 0) * opt.trips)} total mi &middot; {Math.round((opt.routeDurationMinutes || 0) * opt.trips)} min
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: isCheapest ? 'var(--primary, #1f5e3a)' : 'var(--ink, #1c2419)', fontFamily: 'var(--font-mono)' }}>
                          {formatMoney(opt.totalCost)}
                        </div>
                        <div style={{ fontSize: 9, color: 'var(--ink-4, #97a094)' }}>
                          {formatMoney(opt.dumpFees)} dump &middot; {formatMoney(opt.transportCost)} transport
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, color: 'var(--ink-4, #97a094)' }}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </div>
                    </div>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <div style={{ padding: '0 16px 10px', borderTop: '1px solid var(--line, #e0d8c2)', paddingTop: 8 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 11 }}>
                          <DetailRow label="Round-trip" value={`${opt.routeDistanceMiles} mi (${opt.routeDurationMinutes} min)`} />
                          <DetailRow label="Mileage" value={`${formatMoney(opt.mileageCost)} at $${opt.costPerMile}/mi`} />
                          <DetailRow label="Operator" value={`${formatMoney(opt.operatorCost)} at $${opt.operatorCostPerHour}/hr`} />
                          <DetailRow label="Dump fee"
                            value={opt.dumpFeeUnit === 'PER_TON'
                              ? `${formatMoney(opt.dumpFeeRate)}/ton x ${group.quantity}T`
                              : `${formatMoney(opt.dumpFeeRate)}/load x ${opt.trips}`} />
                          {opt.facilityAddress && <DetailRow label="Address" value={opt.facilityAddress} span />}
                        </div>
                        {selectedLocation && opt.facilityLat && (
                          <div style={{ marginTop: 8 }}>
                            <DisposalRouteMap url={url} hmiCoords={HMI_YARD}
                              jobCoords={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                              facilities={[{ lat: opt.facilityLat, lng: opt.facilityLng, name: opt.facilityName }]}
                              height={200} />
                          </div>
                        )}
                      </div>
                    </Collapse>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const ROLLOFF_INITIAL_SHOW = 3

const RolloffComparison = ({ resultGroups, expandedKey, setExpandedKey }) => {
  const [showAll, setShowAll] = useState(false)
  // Merge all roll-off options across material groups, dedup by vendorName + containerSize
  const allRolloffs = []
  const seen = new Set()
  for (const group of resultGroups) {
    for (const ro of (group.rolloffOptions || [])) {
      const key = `${ro.vendorName}_${ro.containerSize}`
      if (seen.has(key)) continue
      seen.add(key)
      allRolloffs.push(ro)
    }
  }
  if (allRolloffs.length === 0) return null

  // For multi-material: sum the cost per vendor+size across all materials
  const isMulti = resultGroups.length > 1
  let displayOptions = allRolloffs

  if (isMulti) {
    const vendorSizeMap = new Map()
    for (const group of resultGroups) {
      for (const ro of (group.rolloffOptions || [])) {
        const key = `${ro.vendorName}_${ro.containerSize}`
        if (!vendorSizeMap.has(key)) {
          vendorSizeMap.set(key, { ...ro, totalCost: 0, dumpFee: 0, materials: [] })
        }
        const entry = vendorSizeMap.get(key)
        entry.totalCost += ro.totalCost
        entry.dumpFee += ro.dumpFee
        entry.materials.push({ material: ro.materialCategory, quantity: ro.quantity, cost: ro.totalCost })
      }
    }
    displayOptions = Array.from(vendorSizeMap.values())
      .filter(o => o.materials.length === resultGroups.length) // only show vendors that cover ALL materials
      .sort((a, b) => a.totalCost - b.totalCost)
  } else {
    displayOptions.sort((a, b) => a.totalCost - b.totalCost)
  }

  if (displayOptions.length === 0) return null

  // Find cheapest our-truck option for comparison
  const cheapestOurTruck = resultGroups.reduce((best, g) => {
    const c = g.cheapest
    if (!c) return best
    if (!best || c.totalCost < best.totalCost) return c
    return best
  }, null)

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8B6914', fontFamily: 'var(--font-mono)' }}>
          ROLL-OFF COMPARISON
        </span>
        <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>
          vendor drops container, we fill, vendor picks up
        </span>
      </div>

      {(showAll ? displayOptions : displayOptions.slice(0, ROLLOFF_INITIAL_SHOW)).map((ro, idx) => {
        const key = `rolloff-${idx}`
        const isExpanded = expandedKey === key
        const savings = cheapestOurTruck ? cheapestOurTruck.totalCost - ro.totalCost : 0

        return (
          <div key={key} style={{
            background: 'var(--surface, #fbf8f1)',
            border: `1px solid ${savings > 0 ? 'rgba(139, 105, 20, .3)' : 'var(--line, #e0d8c2)'}`,
            borderRadius: 10, marginBottom: 6, overflow: 'hidden', cursor: 'pointer',
            boxShadow: savings > 0 ? '0 1px 4px rgba(139,105,20,.1)' : 'none'
          }} onClick={() => setExpandedKey(isExpanded ? null : key)}>
            <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#faf0d4', color: '#8B6914', fontSize: 10, fontWeight: 700, flexShrink: 0,
                border: '1px solid rgba(139,105,20,.2)'
              }}>{ro.containerSize}y</div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink, #1c2419)' }}>{ro.vendorName}</span>
                  <Chip size="small" label={`${ro.containerSize}yd Roll-Off`}
                    variant="outlined" style={{ fontSize: 9, height: 16, fontWeight: 600, color: '#8B6914', borderColor: 'rgba(139,105,20,.3)' }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--ink-4, #97a094)', marginTop: 1 }}>
                  {ro.hauls > 1 ? `${ro.hauls} hauls · ` : ''}{ro.rentalPeriodDays}-day rental
                  {ro.includedTons >= 99 ? ' · No weight limit' : ro.includedTons > 0 ? ` · ${ro.includedTons}T/haul included` : ''}
                  {ro.overagePerTon > 0 && ro.includedTons < 99 && ` · $${ro.overagePerTon}/T overage`}
                </div>
              </div>

              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink, #1c2419)', fontFamily: 'var(--font-mono)' }}>
                  {formatMoney(ro.totalCost)}
                </div>
                <div style={{ fontSize: 9, color: savings > 0 ? '#2f7a3e' : savings < 0 ? '#a9341d' : 'var(--ink-4, #97a094)' }}>
                  {cheapestOurTruck
                    ? savings > 0 ? `saves ${formatMoney(savings)} vs our truck`
                    : savings < 0 ? `${formatMoney(Math.abs(savings))} more than our truck`
                    : 'same as our truck'
                    : ''}
                </div>
              </div>

              <div style={{ flexShrink: 0, color: 'var(--ink-4, #97a094)' }}>
                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </div>
            </div>

            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <div style={{ padding: '0 16px 10px', borderTop: '1px solid var(--line, #e0d8c2)', paddingTop: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 11 }}>
                  <DetailRow label="Hauls" value={ro.hauls > 1 ? `${ro.hauls} × ${formatMoney(ro.haulFeePerHaul || ro.haulFee)} = ${formatMoney(ro.haulFee)}` : formatMoney(ro.haulFee)} />
                  <DetailRow label="Dump fee" value={ro.dumpFee > 0 ? formatMoney(ro.dumpFee) : 'Included'} />
                  <DetailRow label="Rental" value={ro.rentalRate > 0 ? `${formatMoney(ro.rentalRate)} (${ro.rentalPeriodDays} days)` : `Included (${ro.rentalPeriodDays} days)`} />
                  <DetailRow label="Extra day" value={ro.extraDayRate > 0 ? `${formatMoney(ro.extraDayRate)}/day` : 'N/A'} />
                  {ro.includedTons >= 99
                    ? <DetailRow label="Weight" value="No limit" />
                    : ro.includedTons > 0 && <DetailRow label="Included" value={`${ro.includedTons}T per haul (${ro.includedTons * (ro.hauls || 1)}T total)`} />}
                  {ro.overagePerTon > 0 && ro.includedTons < 99 && <DetailRow label="Overage" value={`${formatMoney(ro.overagePerTon)}/ton over ${ro.includedTons}T/haul`} />}
                  {ro.dumpFeePerTon > 0 && <DetailRow label="Dump rate" value={`${formatMoney(ro.dumpFeePerTon)}/ton × ${ro.quantity}T`} />}
                  {ro.notes && <DetailRow label="Notes" value={ro.notes} span />}
                </div>
              </div>
            </Collapse>
          </div>
        )
      })}

      {displayOptions.length > ROLLOFF_INITIAL_SHOW && (
        <Button size="small" onClick={() => setShowAll(!showAll)}
          style={{ fontSize: 11, textTransform: 'none', color: '#8B6914', fontWeight: 600, marginTop: 4 }}>
          {showAll ? 'Show fewer' : `Show ${displayOptions.length - ROLLOFF_INITIAL_SHOW} more roll-off options`}
        </Button>
      )}
    </div>
  )
}

const DetailRow = ({ label, value, span }) => (
  <div style={{ ...(span ? { gridColumn: '1 / -1' } : {}) }}>
    <span style={{ fontWeight: 600, color: 'var(--ink-3, #6f7a6b)', fontSize: 10, marginRight: 4 }}>{label}:</span>
    <span style={{ color: 'var(--ink-2, #3d463a)' }}>{value}</span>
  </div>
)

export default DisposalQuickCalculator
